import axios from 'axios';
import { supabase } from '../auth/supabaseClient';
import { getActiveTenantId, TENANT_SELECTION_REQUIRED_EVENT } from './activeTenant';
import { PRODUCT_NOT_ENABLED_EVENT, PRODUCT_NOT_ENABLED_CODE } from './productGate';

// API base URLs - using setupProxy.js for development routing
const LIBRARY_API_BASE = '/api/library';

// Capture API base URL - always use absolute URL to capture-app
const CAPTURE_API_BASE_URL = process.env.REACT_APP_CAPTURE_API_BASE_URL || '';

// ── Which requests carry the user's credentials ──────────────────────
// The bearer token and the active tenant go to OUR OWN /api/ routes and nowhere
// else. The decision is made on the URL the browser will actually request, not
// on the text of config.url: `//host/api/x` and `/\host/api/x` both begin with a
// slash and both leave this origin, and a relative url under a foreign baseURL
// never starts with anything suspicious at all. So the url is joined to baseURL
// the way axios joins them, resolved with the URL parser, and compared by
// origin and normalised pathname.
//
// `/api/` rather than a list of prefixes: capture-service has routes outside
// /api/capture (/api/ocr-words), and a list had to be extended by hand for each
// one or the call went out tokenless and the 401 signed the user out.
//
// The absolute-base branch is for a deployment that points the UI at a
// capture-service on another origin. Guarded on a non-empty base, because
// startsWith('') is true of every url.
const ABSOLUTE_URL = /^([a-z][a-z\d+\-.]*:)?\/\//i;

export function shouldAttachAuth(config) {
  const url = typeof config?.url === 'string' ? config.url : '';
  if (CAPTURE_API_BASE_URL && url.startsWith(CAPTURE_API_BASE_URL)) return true;
  if (typeof window === 'undefined' || !window.location) return false;

  const origin = window.location.origin;
  const baseURL = typeof config?.baseURL === 'string' ? config.baseURL : '';
  // axios's own rule: baseURL applies unless the url is absolute.
  const requested = baseURL && !ABSOLUTE_URL.test(url)
    ? (url ? `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}` : baseURL)
    : url;

  let resolved;
  try {
    resolved = new URL(requested, origin);
  } catch {
    return false;
  }
  return resolved.origin === origin && resolved.pathname.startsWith('/api/');
}

axios.interceptors.request.use(async (config) => {
  if (shouldAttachAuth(config)) {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // non-fatal — request proceeds without token
    }
    // Attach the active tenant (2026-06-05 fix). Tenant-agnostic switcher calls
    // don't need it, but sending it is harmless there. Source is swappable
    // (activeTenant.js) without touching this attach.
    const activeTenantId = getActiveTenantId();
    if (activeTenantId) {
      config.headers['x-tenant-id'] = activeTenantId;
    }
  }
  return config;
});

// ── A 401 means "maybe expired", not "signed out" ────────────────────
// One rejected request used to end the session: sign out, hard redirect, and
// whatever the user was doing was gone. Now a 401 on a request that carried our
// token is answered by refreshing the session and sending the request again,
// once. The user is signed out only when the session is known to be gone:
//
//   refresh says the session is dead        → sign out
//   the retried request is rejected as well → sign out
//   refresh could not be attempted or
//   answered (network, 5xx, rate limit)     → reject this call, stay signed in
//
// AUTH AND REFRESH CALLS MUST NEVER USE THE GLOBAL AXIOS INSTANCE. They go
// through supabase-js, which has its own fetch. A sign-in or refresh call made
// with `axios` would pass through this interceptor, and its own 401 would start
// a refresh from inside a refresh.
const LOGIN_PATH = '/login';

function sentBearer(config) {
  const headers = config?.headers;
  if (!headers) return null;
  const value = typeof headers.get === 'function'
    ? headers.get('Authorization')
    : headers.Authorization || headers.authorization;
  return typeof value === 'string' && value.startsWith('Bearer ') ? value.slice(7) : null;
}

/**
 * Is this refresh failure proof that the session is gone?
 *
 * Only a definite answer from the auth server counts: a 4xx (the refresh token
 * is revoked, expired or already used) or supabase-js finding no session to
 * refresh. A fetch that never got an answer, a 5xx and a 429 say nothing about
 * the session, and signing someone out because their wifi dropped is the bug
 * this block exists to remove.
 */
function isSessionGone(error) {
  if (error?.name === 'AuthRetryableFetchError') return false;
  const status = error?.status;
  return typeof status === 'number' && status >= 400 && status < 500 && status !== 429;
}

// ONE refresh at a time. Supabase rotates the refresh token on use, so five
// requests failing together must share a single refresh rather than race five.
let refreshInFlight = null;

/** Resolves { status: 'ok', token } | { status: 'dead' } | { status: 'unknown' }. */
function refreshOnce() {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error) return { status: isSessionGone(error) ? 'dead' : 'unknown' };
        const token = data?.session?.access_token;
        return token ? { status: 'ok', token } : { status: 'dead' };
      } catch {
        // The call itself threw: nothing was learned about the session.
        return { status: 'unknown' };
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

// Once per page. A burst of rejected requests must produce one sign-out and one
// navigation, including the ones that arrive after the first has started.
let signingOut = false;

async function signOutAndRedirect() {
  if (signingOut) return;
  signingOut = true;
  try {
    // 'local': end THIS browser's session. The default scope revokes every
    // session the user has, which would sign them out of the Library too.
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    /* the redirect below is what matters */
  }
  window.location.href = LOGIN_PATH;
}

async function currentToken() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const payload = error.response?.data;
    const sent = sentBearer(config);
    if (
      error.response?.status === 401 &&
      (payload?.error === 'invalid_token' || payload?.error === 'unauthorized') &&
      // A request that carried no token of ours was never going to be accepted,
      // and says nothing about the session. Nor does one sent somewhere we do
      // not attach credentials to — and that one must not be re-sent with them.
      sent && shouldAttachAuth(config) &&
      !signingOut
    ) {
      // Rejected again with a token minted moments ago: the session is not
      // usable, whatever the refresh said.
      if (config._authRetried) {
        await signOutAndRedirect();
        return Promise.reject(error);
      }

      // A late 401 from a burst that has already been refreshed: the session
      // holds a newer token than this request carried, so use it.
      const current = await currentToken();
      let token = current && current !== sent ? current : null;

      if (!token) {
        const refreshed = await refreshOnce();
        if (refreshed.status === 'dead') {
          await signOutAndRedirect();
          return Promise.reject(error);
        }
        if (refreshed.status !== 'ok') {
          return Promise.reject(error);
        }
        token = refreshed.token;
      }

      // Exactly once. config.data is what was sent the first time — a FormData
      // is the same object, a JSON body is already a string — so it replays.
      config._authRetried = true;
      if (typeof config.headers?.set === 'function') {
        config.headers.set('Authorization', `Bearer ${token}`);
      } else {
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
      }
      return axios.request(config);
    }
    // LB-14: the tenant's workspace does not have the Capture product. Surface a
    // full-page explanation rather than a shell whose every request fails.
    //
    // Matched on the error CODE and the status TOGETHER — never on 403 alone,
    // and never on the human-facing message. capture-service also returns 403
    // with error 'forbidden' (no tenant on a gated route) and the role gates
    // return their own 403s; those are different conditions and must keep their
    // existing behaviour.
    if (
      error.response?.status === 403 &&
      error.response.data?.error === PRODUCT_NOT_ENABLED_CODE
    ) {
      try {
        window.dispatchEvent(new CustomEvent(PRODUCT_NOT_ENABLED_EVENT));
      } catch {
        /* no window */
      }
    }
    // Multi-tenant user with no active tenant selected (2026-06-05 fix, R5):
    // surface the switcher rather than an error toast / empty screen.
    if (error.response?.status === 409 && error.response.data?.error === 'tenant_selection_required') {
      try {
        window.dispatchEvent(
          new CustomEvent(TENANT_SELECTION_REQUIRED_EVENT, {
            detail: { tenants: error.response.data.tenants || [] },
          }),
        );
      } catch {
        /* no window */
      }
    }
    return Promise.reject(error);
  },
);

// ── Tenant switcher API ──────────────────────────────────────────────
// The tenant LIST moved to identityContract.listMyTenants() (B-2c, self-service
// seam read). Only the SELECT write remains capture-local (it validates
// membership server-side and persists the SHARED current_tenant_id).
const CAPTURE_BASE = CAPTURE_API_BASE_URL;
export const tenantAPI = {
  // persists the SHARED current_tenant_id; returns { tenantId, role }
  select: async (tenantId) => {
    const res = await axios.post(`${CAPTURE_BASE}/api/capture/tenants/select`, { tenantId });
    return res.data;
  },
};

// Dedicated Library API axios instance with Bearer token auth
const libraryApi = axios.create({
  baseURL: process.env.REACT_APP_LIBRARY_BASE_PATH || '/api/library'
});

// Inject the logged-in user's Supabase access token on every Library request
// (mirrors the capture interceptor above and identityContract.js seam reads).
libraryApi.interceptors.request.use(async (config) => {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});



// Capture API calls
export const captureAPI = {
  // Upload file to Capture service.
  // options.purpose: 'production' (default) | 'template_source'
  //   Pass 'template_source' when uploading from Template Studio so the
  //   document is excluded from the Processing tab and skips auto-extraction.
  // options.workflowId: the workflow this document is being uploaded into.
  //   Required for production uploads under Option C (canonical extraction
  //   trigger) — see decisions/2026-05-15-canonical-extraction-trigger.md.
  //   Template Studio uploads omit this.
  uploadFile: async (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (options.purpose) {
      formData.append('purpose', options.purpose);
    }
    if (options.workflowId) {
      formData.append('workflow_id', options.workflowId);
    }
    // Gate C "Upload as new": after the user chose to proceed past the duplicate
    // prompt, re-send with overrideDuplicate so the server bypasses the 409 and
    // processes the file as a fresh document (OCR enqueued).
    if (options.overrideDuplicate) {
      formData.append('overrideDuplicate', 'true');
    }

    const url = `${CAPTURE_API_BASE_URL}/api/capture/upload`;
    console.log('[Capture API] Upload endpoint', url);

    try {
      const response = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('[Capture API] Upload response', response.data);
      const data = response.data;
      // Normalize: ensure `document` alias exists for callers expecting it
      if (data?.captureDocument && !data.document) {
        data.document = data.captureDocument;
      }
      return data;
    } catch (error) {
      console.error('[Capture API] Upload failed:', error);
      
      // Surface server JSON details if present
      if (error.response?.data) {
        console.error('[Capture API] Server error details:', {
          status: error.response.status,
          data: error.response.data,
          bucket: error.response.data?.bucket,
          code: error.response.data?.code,
          suggestion: error.response.data?.suggestion
        });
        
        // Re-throw with enhanced error message
        const enhancedError = new Error(
          error.response.data?.details || 
          error.response.data?.error || 
          `Upload failed with status ${error.response.status}`
        );
        enhancedError.serverDetails = error.response.data;
        enhancedError.status = error.response.status;
        throw enhancedError;
      }
      
      throw error;
    }
  },

  // List capture documents for tenant
  listDocuments: async (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.status) searchParams.set('status', params.status);
    const qs = searchParams.toString();
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents${qs ? `?${qs}` : ''}`;
    const response = await axios.get(url);
    return response.data;
  },

  // Get capture document metadata
  getDocument: async (documentId) => {
    console.log('[Capture API] Fetching capture document:', documentId);
    
    try {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}`;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('[Capture API] Request failed:', error);
      if (error.response) {
        console.error('[Capture API] Response status:', error.response.status);
        console.error('[Capture API] Response data:', error.response.data);
      }
      throw error;
    }
  },

  // Update capture document metadata
  updateDocument: async (documentId, data) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}`;
    const response = await axios.patch(url, data);
    return response.data;
  },

  // v1a step 6: read-only advisory for the type-correction control. Answers,
  // before the user commits: target department, whether it changes, and whether
  // the CALLING user still sees the document afterwards. options.signal takes
  // an AbortController signal so a superseded selection's response can be
  // cancelled rather than racing a stale retainsAccess into the warning.
  typeCorrectionPreflight: async (documentId, type, options = {}) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/type-correction-preflight?type=${encodeURIComponent(type)}`;
    const response = await axios.get(url, { signal: options.signal });
    return response.data;
  },

  // ── Guided setup v2: type declarations ────────────────────────────────
  //
  // The "it's something new" answer to the classification panel's question.
  // NOT a correction: the declared label names a type that does not exist yet,
  // so it cannot go through PATCH document_type (the registry check rejects an
  // unknown key). The server records the assertion and stops — nothing is
  // classified and no schema is minted here.

  // Returns { ok, declaration } where declaration is the PENDING row or null.
  // Member-open: a reviewer who cannot mint must still be able to see that a
  // declaration is already queued, or the UI offers a duplicate that 409s.
  getTypeDeclaration: async (documentId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/type-declaration`;
    const response = await axios.get(url);
    return response.data;
  },

  // 201 → { ok: true, declaration }. 409 declaration_pending carries the row
  // that won the race in the SAME `declaration` key, so the caller can show the
  // existing declaration instead of re-prompting. Builder-only server-side
  // (accessPolicy buildOnly) — declaring is a request to mint tenant schema.
  createTypeDeclaration: async (documentId, { declared_label, field_wishes }) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/type-declaration`;
    // field_wishes is OMITTED, never sent as null. The server schema is
    // .strict() with an .optional() (not .nullable()) field_wishes, so an
    // explicit null is a 400 — the caller should not have to know that.
    const body = { declared_label };
    if (typeof field_wishes === 'string' && field_wishes !== '') body.field_wishes = field_wishes;
    const response = await axios.post(url, body);
    return response.data;
  },

  // ── Split review (slice 3b): verify an already-split container ─────────
  //
  // Returns { ok, container, children, partition, proposal, readiness }.
  // readiness.reason names the precondition that stops a verdict, if any.
  getSplitReview: async (documentId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/split-review`;
    const response = await axios.get(url);
    return response.data;
  },

  // outcome: 'agreed' | 'flagged'. A 409 is a REFUSAL the page shows (its body
  // names the reason, and carries the prior verdict for already_confirmed), so
  // it is returned rather than thrown. Anything else still throws.
  // outcome: 'agreed' | 'flagged' | 'corrected'; ranges only with 'corrected'.
  // A held batch answers 202: the verdict is recorded and its documents are
  // being created in the background — poll getSplitReview.
  recordSplitVerdict: async (documentId, outcome, ranges) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/split-review`;
    try {
      const response = await axios.post(url, ranges ? { outcome, ranges } : { outcome });
      return response.data;
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data) return err.response.data;
      throw err;
    }
  },

  // Re-run the re-entry of a STRANDED confirm (readiness 'confirm_stranded').
  // Writes no verdict. 202 → poll getSplitReview; a 409 is returned, not thrown.
  retrySplitReentry: async (documentId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/split-review/retry`;
    try {
      const response = await axios.post(url, {});
      return response.data;
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data) return err.response.data;
      throw err;
    }
  },

  // ── Guided setup v2: the proposal ────────────────────────────────────
  //
  // The proposal is SERVER-AUTHORED and SERVER-STORED. This client reads it and
  // sends bounded edits back by id — it never posts a proposal body. That is
  // the security shape of the whole feature: apply mints tenant-global
  // definitions, so a body-carried proposal would let a builder mint arbitrary
  // schema by editing JSON in devtools, bypassing the AI call, the reuse-first
  // matching and the collision check.

  // Returns { ok, proposal_id, proposal } — both null when none is open.
  // Member-open (falls through to documents:list): reading mints nothing.
  getSetupProposal: async (documentId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/setup-proposal`;
    const response = await axios.get(url);
    return response.data;
  },

  // Authors a proposal from a PENDING declaration and stores it. Builder-only
  // server-side, and it spends AI budget — one call, 4-7 seconds.
  createSetupProposal: async (documentId, declarationId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/setup-proposal`;
    const response = await axios.post(url, { declaration_id: declarationId });
    return response.data;
  },

  // Applies a stored proposal. `edits` is a DELTA — detection_phrases,
  // resolutions, skip_ops — omitted entirely when empty so the server sees the
  // stored proposal unmodified. 200 on full success, 207 on a partial apply
  // (some ops landed; re-applying completes it, the ops being idempotent).
  applySetupProposal: async (documentId, proposalId, edits) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/setup-apply`;
    const body = { proposal_id: proposalId };
    if (edits && Object.keys(edits).length > 0) body.edits = edits;
    // 207 is a SUCCESS status for axios only if we say so: its default
    // validateStatus rejects anything >= 300, which would turn a partial apply
    // into a thrown error and hide the ops that did land.
    const response = await axios.post(url, body, {
      validateStatus: (status) => status === 200 || status === 207,
    });
    return { ...response.data, status: response.status };
  },

  // Get capture document file as blob (backward compatibility)
  getDocumentFile: async (documentId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/file`;
    console.log('[Capture API] Fetching document file as blob:', url);
    
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const contentType = response.headers['content-type'];
      console.log('[Capture API] Document file response:', {
        contentType,
        size: response.data.size,
        type: response.data.type
      });
      return response.data;
    } catch (error) {
      console.error('[Capture API] Document file request failed:', error);
      throw error;
    }
  },

  // Get capture document file URL as JSON (PDF.js friendly)
  getDocumentFileUrl: async (documentId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/file-url`;
    console.log('[Capture API] Fetching document file URL:', url);
    
    try {
      const response = await axios.get(url);
      const data = response.data;
      
      // Validate response structure
      if (!data.ok || !data.url || typeof data.url !== 'string') {
        throw new Error('Invalid file URL response structure');
      }
      
      // Validate URL format
      if (!data.url.startsWith('http')) {
        throw new Error(`Invalid file URL format: ${data.url.substring(0, 50)}...`);
      }
      
      console.log('[Capture API] Document file URL response:', {
        ok: data.ok,
        urlLength: data.url.length,
        urlPrefix: data.url.substring(0, 50) + '...',
        expiresIn: data.expiresIn,
        filename: data.filename,
        mimeType: data.mimeType
      });
      
      return data;
    } catch (error) {
      console.error('[Capture API] Document file URL request failed:', error);
      
      // Log additional debug info for HTML responses
      if (error.response?.data && typeof error.response.data === 'string') {
        const preview = error.response.data.substring(0, 120);
        console.error('[Capture API] Response preview (first 120 chars):', preview);
      }
      
      throw error;
    }
  },

  // Get processing details for OCR pipeline
  getProcessingDetails: async (documentId) => {
    console.log('[Capture API] Fetching processing details:', documentId);
    
    try {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/processing`;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('[Capture API] Processing details request failed:', error);
      throw error;
    }
  },

  // Get OCR text
  getOCRText: async (documentId, limit) => {
    console.log('[Capture API] Fetching OCR text:', documentId);
    
    try {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/ocr-text${limit ? `?limit=${limit}` : ''}`;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('[Capture API] OCR text request failed:', error);
      throw error;
    }
  },

  // OCR Words API - calls capture-app backend directly
  getOcrWords: async (params) => {
    // No engine default: omitting the filter returns words from every
    // acquisition route (paddle, tesseract, text_layer). Defaulting to 'paddle'
    // made the overlay silently empty for any document read another way.
    const { documentId, pageIndex, engine } = params;
    
    const searchParams = new URLSearchParams();
    searchParams.append('documentId', documentId);
    searchParams.append('pageIndex', pageIndex.toString());
    if (engine) {
      searchParams.append('engine', engine);
    }

    const url = `${CAPTURE_API_BASE_URL}/api/ocr-words?${searchParams}`;
    console.log('[Capture API] Fetching OCR words:', url);
    
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('[Capture API] OCR words request failed:', error);
      throw error;
    }
  },

  // OCR Word Statistics - calls capture-app backend directly.
  // engine is OPTIONAL and intentionally NOT defaulted: omitting it makes the
  // server count words across ALL OCR engines, so readiness polling (template
  // create/edit) passes for docs OCR'd by tesseract/merged/paddle alike. Pass
  // an explicit engine only for engine-specific debugging.
  getOcrWordStats: async (params) => {
    const { documentId, engine } = params;

    const searchParams = new URLSearchParams();
    searchParams.append('documentId', documentId);
    if (engine) {
      searchParams.append('engine', engine);
    }

    const url = `${CAPTURE_API_BASE_URL}/api/ocr-words/stats?${searchParams}`;
    console.log('[Capture API] Fetching OCR word stats:', url);
    
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('[Capture API] OCR word stats request failed:', error);
      throw error;
    }
  },

  classifyDocument: async (documentId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/classify`;
    console.log('[Capture API] Classifying document:', url);
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.warn('[Capture API] Classification failed:', error?.message);
      return { ok: false, documentType: 'other', confidence: 0 };
    }
  },

  // Template Studio API methods - calls capture-app backend directly
  templates: {
    list: async (params = {}) => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.append('page', params.page.toString());
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.search) searchParams.append('search', params.search);
      if (params.category) searchParams.append('category', params.category);
      
      const queryString = searchParams.toString();
      const url = `${CAPTURE_API_BASE_URL}/api/capture/templates${queryString ? `?${queryString}` : ''}`;
      
      try {
        const response = await axios.get(url);
        return response.data;
      } catch (error) {
        console.error('[Capture API] Template list request failed:', error);
        throw error;
      }
    },

    get: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${id}`;
      
      try {
        const response = await axios.get(url);
        return response.data;
      } catch (error) {
        console.error('[Capture API] Template get request failed:', error);
        throw error;
      }
    },

    create: async (templateData) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/templates`;
      
      try {
        const response = await axios.post(url, templateData);
        return response.data;
      } catch (error) {
        console.error('[Capture API] Template create request failed:', error);
        throw error;
      }
    },

    update: async (id, templateData) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${id}`;
      
      try {
        const response = await axios.put(url, templateData);
        return response.data;
      } catch (error) {
        console.error('[Capture API] Template update request failed:', error);
        throw error;
      }
    },

    delete: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/${id}`;
      
      try {
        const response = await axios.delete(url);
        return response.data;
      } catch (error) {
        console.error('[Capture API] Template delete request failed:', error);
        throw error;
      }
    }
  },


  getDocumentExtractionNotes: async (documentId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/extraction-notes`;
    const res = await axios.get(url);
    return res.data;
  },

  saveDocumentExtractionNotes: async (documentId, notes) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/extraction-notes`;
    const res = await axios.patch(url, { notes });
    return res.data;
  },

  // Batch API methods
  batches: {
    upload: async (files, { batchName, priority, workflowId } = {}) => {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      if (batchName) formData.append('batch_name', batchName);
      if (priority) formData.append('priority', priority);
      // workflowId required under Option C (canonical extraction trigger).
      // See decisions/2026-05-15-canonical-extraction-trigger.md.
      if (workflowId) formData.append('workflow_id', workflowId);

      const url = `${CAPTURE_API_BASE_URL}/api/capture/upload/batch`;
      console.log('[Capture API] Batch upload:', { fileCount: files.length, batchName, workflowId });

      const response = await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },

    list: async (params = {}) => {
      const searchParams = new URLSearchParams();
      if (params.status) searchParams.append('status', params.status);
      if (params.limit) searchParams.append('limit', params.limit.toString());
      if (params.offset) searchParams.append('offset', params.offset.toString());

      const qs = searchParams.toString();
      const url = `${CAPTURE_API_BASE_URL}/api/capture/batches${qs ? `?${qs}` : ''}`;
      const response = await axios.get(url);
      return response.data;
    },

    get: async (batchId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/batches/${batchId}`;
      const response = await axios.get(url);
      return response.data;
    },

    cancel: async (batchId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/batches/${batchId}/cancel`;
      const response = await axios.post(url);
      return response.data;
    },

    action: async (documentIds, action, extra = {}) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/batch-action`;
      const response = await axios.post(url, {
        document_ids: documentIds,
        action,
        ...extra,
      });
      return response.data;
    },

    updatePriority: async (documentId, priority) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/priority`;
      const response = await axios.put(url, { priority });
      return response.data;
    },

    delete: async (batchId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/batches/${batchId}`;
      const response = await axios.delete(url);
      return response.data;
    },
  },

  // Destinations API methods
  destinations: {
    list: async (params = {}) => {
      const searchParams = new URLSearchParams();
      if (params.includeInactive) searchParams.append('includeInactive', 'true');
      // Adds `deleted: [{ id, name }]` to the response (the workflow designer).
      if (params.includeDeleted) searchParams.append('includeDeleted', 'true');
      const qs = searchParams.toString();
      const url = `${CAPTURE_API_BASE_URL}/api/capture/destinations${qs ? `?${qs}` : ''}`;
      const response = await axios.get(url);
      return response.data;
    },

    create: async (data) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/destinations`;
      const response = await axios.post(url, data);
      return response.data;
    },

    update: async (id, data) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/destinations/${id}`;
      const response = await axios.put(url, data);
      return response.data;
    },

    delete: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/destinations/${id}`;
      const response = await axios.delete(url);
      return response.data;
    },

    /**
     * What deleting a destination would affect: { destination, workflows }, the
     * workflows being those whose Publish node names it, each with a status
     * (active, draft, inactive). Admin-only, read-only.
     */
    deletePreview: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/destinations/${id}/delete-preview`;
      const response = await axios.get(url);
      return response.data;
    },

    /**
     * Delete a destination. Admin-only. Its publish history is kept. NOT the
     * `delete` above, which is the older name for Deactivate and still is.
     */
    remove: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/destinations/${id}/delete`;
      const response = await axios.post(url, {});
      return response.data;
    },

    test: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/destinations/${id}/test`;
      const response = await axios.post(url);
      return response.data;
    },

    getFieldKeys: async () => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/destinations/field-keys`;
      const response = await axios.get(url);
      return response.data;
    },

    getTargetFields: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/destinations/${id}/target-fields`;
      const response = await axios.get(url);
      return response.data;
    },

    suggestFieldMapping: async (id, targetFields) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/destinations/${id}/suggest-mapping`;
      const response = await axios.post(url, { targetFields });
      return response.data;
    },
  },

  // Workflows API methods
  workflows: {
    list: async () => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows`;
      const response = await axios.get(url);
      return response.data;
    },

    create: async (data) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows`;
      const response = await axios.post(url, data);
      return response.data;
    },

    get: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/${id}`;
      const response = await axios.get(url);
      return response.data;
    },

    update: async (id, data) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/${id}`;
      const response = await axios.put(url, data);
      return response.data;
    },

    delete: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/${id}`;
      const response = await axios.delete(url);
      return response.data;
    },

    publish: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/${id}/publish`;
      const response = await axios.post(url);
      return response.data;
    },

    unpublish: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/${id}/unpublish`;
      const response = await axios.post(url);
      return response.data;
    },

    duplicate: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/${id}/duplicate`;
      const response = await axios.post(url);
      return response.data;
    },

    // Distinct document types of the documents this workflow has processed —
    // pre-ticks the Publish node's "+ New index field" modal.
    getDocumentTypes: async (id) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/${id}/document-types`;
      const response = await axios.get(url);
      return response.data;
    },

    getNodeTypes: async () => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/node-types`;
      const response = await axios.get(url);
      return response.data;
    },

    getTemplates: async () => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/templates`;
      const response = await axios.get(url);
      return response.data;
    },

    getFieldKeys: async () => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/field-keys`;
      const response = await axios.get(url);
      return response.data;
    },

    execute: async (workflowId, documentId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/${workflowId}/execute`;
      const response = await axios.post(url, { documentId });
      return response.data;
    },

    executeBatch: async (workflowId, documentIds) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/${workflowId}/execute-batch`;
      const response = await axios.post(url, { documentIds });
      return response.data;
    },

    getExecutions: async (workflowId, params = {}) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflows/${workflowId}/executions`;
      const searchParams = new URLSearchParams();
      if (params.limit) searchParams.set('limit', String(params.limit));
      if (params.status) searchParams.set('status', params.status);
      const qs = searchParams.toString();
      const response = await axios.get(qs ? `${url}?${qs}` : url);
      return response.data;
    },

    getExecution: async (executionId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflow-executions/${executionId}`;
      const response = await axios.get(url);
      return response.data;
    },

    resumeExecution: async (executionId, fields) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflow-executions/${executionId}/resume`;
      const body = fields && Object.keys(fields).length > 0 ? { fields } : {};
      const response = await axios.post(url, body);
      return response.data;
    },

    cancelExecution: async (executionId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflow-executions/${executionId}/cancel`;
      const response = await axios.post(url);
      return response.data;
    },

    getReviewQueue: async (params = {}) => {
      const searchParams = new URLSearchParams();
      if (params.status) searchParams.set('status', params.status);
      if (params.limit) searchParams.set('limit', String(params.limit));
      const qs = searchParams.toString();
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflow-executions/review-queue${qs ? `?${qs}` : ''}`;
      const response = await axios.get(url);
      return response.data;
    },

    updateReviewFields: async (executionId, fields) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflow-executions/${executionId}/review-fields`;
      const response = await axios.patch(url, { fields });
      return response.data;
    },

    getExecutionSteps: async (executionId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflow-executions/${executionId}/steps`;
      const response = await axios.get(url);
      return response.data;
    },

    retryNode: async (executionId, nodeId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflow-executions/${executionId}/retry-node`;
      const response = await axios.post(url, { nodeId });
      return response.data;
    },

    // Re-run the Publish node a run failed at, with that node's own config.
    // The server refuses (409) when the run did not fail at a Publish node.
    retryFailedPublish: async (executionId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflow-executions/${executionId}/retry-node`;
      const response = await axios.post(url, { failedPublish: true });
      return response.data;
    },

    // Re-run a failed run from the node it failed at (admin-only on the
    // server). No body: the server decides the node. A refusal is a 409 whose
    // `error` says why.
    retryFailedNode: async (executionId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflow-executions/${executionId}/retry-failed-node`;
      const response = await axios.post(url);
      return response.data;
    },

    // A document's workflow executions, newest first.
    listExecutionsForDocument: async (documentId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflow-executions?document_id=${encodeURIComponent(documentId)}&limit=5`;
      const response = await axios.get(url);
      return response.data;
    },

    rewindExecution: async (executionId, targetNodeId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/workflow-executions/${executionId}/rewind`;
      const response = await axios.post(url, { targetNodeId });
      return response.data;
    },
  },

  // Processing Monitor API methods
  processingMonitor: {
    getDocuments: async (params = {}) => {
      const searchParams = new URLSearchParams();
      if (params.batchId) searchParams.set('batch_id', params.batchId);
      if (params.status) searchParams.set('status', params.status);
      if (params.limit) searchParams.set('limit', String(params.limit));
      if (params.since) searchParams.set('since', params.since);
      const qs = searchParams.toString();
      const url = `${CAPTURE_API_BASE_URL}/api/capture/processing-monitor/documents${qs ? `?${qs}` : ''}`;
      const response = await axios.get(url);
      return response.data;
    },

    /**
     * B4 — the two decisions a reviewer can make about a held duplicate.
     *
     * NOT under /processing-monitor even though the Held tab lives there. These
     * act on the DOCUMENT — its status, its OCR job, its execution, and the
     * customer's file in their drive — rather than on the monitor's view of it,
     * and they carry their own access-policy rule (documents:duplicate-review,
     * POST-only, user mode) precisely so that "a human decides" is enforceable
     * rather than implied.
     */
    processDuplicate: async (documentId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${encodeURIComponent(documentId)}/duplicate/process`;
      const response = await axios.post(url);
      return response.data;
    },

    discardDuplicate: async (documentId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${encodeURIComponent(documentId)}/duplicate/discard`;
      const response = await axios.post(url);
      return response.data;
    },

    retryOcr: async (documentId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/processing-monitor/documents/${documentId}/retry-ocr`;
      const response = await axios.post(url);
      return response.data;
    },

    retryExtraction: async (documentId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/processing-monitor/documents/${documentId}/retry-extraction`;
      const response = await axios.post(url);
      return response.data;
    },

    deleteDocument: async (documentId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/processing-monitor/documents/${documentId}`;
      const response = await axios.delete(url);
      return response.data;
    },

    bulkDelete: async (documentIds) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/processing-monitor/documents/bulk-delete`;
      const response = await axios.post(url, { documentIds });
      return response.data;
    },

    setRetainIndefinitely: async (documentId, retainIndefinitely) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/processing-monitor/documents/${documentId}/retain`;
      const response = await axios.patch(url, { retain_indefinitely: retainIndefinitely });
      return response.data;
    },
  },

  // Extraction Insights API methods
  extractionInsights: {
    getSummary: async (params = {}) => {
      const query = new URLSearchParams();
      if (params.since) query.set('since', params.since);
      if (params.until) query.set('until', params.until);
      const qs = query.toString();
      const url = `${CAPTURE_API_BASE_URL}/api/capture/extraction-insights${qs ? '?' + qs : ''}`;
      const res = await axios.get(url);
      return res.data;
    },
  },

  // Field Library API
  getFieldLibrary: async (scope) => {
    const base = `${CAPTURE_API_BASE_URL}/api/capture/templates/field-library`;
    const url = scope ? `${base}?scope=${encodeURIComponent(scope)}` : base;
    const response = await axios.get(url);
    return response.data;
  },

  createFieldLibraryEntry: async (data) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/field-library`;
    const response = await axios.post(url, data);
    return response.data;
  },

  updateFieldLibraryEntry: async (id, data) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/field-library/${id}`;
    const response = await axios.patch(url, data);
    return response.data;
  },

  // Field-level delete of a custom field (removes the iim_fields row + all its associations).
  // 409 if the field is in use (template / extracted values); 403 for system/global fields.
  deleteCustomField: async (fieldKey) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/field-library/field/${encodeURIComponent(fieldKey)}`;
    const response = await axios.delete(url);
    return response.data;
  },

  addFieldDocTypeAssociation: async (fieldKey, docTypeKey, ownerDeptKey) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/field-library/${fieldKey}/associations`;
    const res = await axios.post(url, { doc_type_key: docTypeKey, owner_dept_key: ownerDeptKey });
    return res.data;
  },

  removeFieldDocTypeAssociation: async (fieldKey, docTypeKey) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/field-library/${fieldKey}/associations/${docTypeKey}`;
    const res = await axios.delete(url);
    return res.data;
  },

  updateFieldOwner: async (fieldKey, ownerDeptKey) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/field-library/${fieldKey}/owner`;
    const res = await axios.patch(url, { owner_dept_key: ownerDeptKey });
    return res.data;
  },

  // Document Types
  getDocumentTypes: async () => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/document-types`;
    const res = await axios.get(url);
    return res.data;
  },
  createDocumentType: async (data) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/document-types`;
    const res = await axios.post(url, data);
    return res.data;
  },
  updateDocumentType: async (id, data) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/document-types/${id}`;
    const res = await axios.patch(url, data);
    return res.data;
  },
  deleteDocumentType: async (id) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/document-types/${id}`;
    const res = await axios.delete(url);
    return res.data;
  },
  // Track 2a: a doc type's ADDITIONAL department-visibility set (read widening).
  getDocumentTypeDepartments: async (id) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/document-types/${id}/departments`;
    const res = await axios.get(url);
    return res.data;
  },
  setDocumentTypeDepartments: async (id, departments) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/document-types/${id}/departments`;
    const res = await axios.put(url, { departments });
    return res.data;
  },

  // Departments
  getDepartments: async () => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/departments`;
    const res = await axios.get(url);
    return res.data;
  },
  createDepartment: async (data) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/departments`;
    const res = await axios.post(url, data);
    return res.data;
  },
  updateDepartment: async (id, data) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/departments/${id}`;
    const res = await axios.patch(url, data);
    return res.data;
  },
  deleteDepartment: async (id) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/templates/departments/${id}`;
    const res = await axios.delete(url);
    return res.data;
  },

  // Generic POST JSON helper (used by AI refine etc.)
  postJson: async (path, body) => {
    const url = `${CAPTURE_API_BASE_URL}${path}`;
    const response = await axios.post(url, body);
    return response.data;
  },

  // Publish API methods
  publish: {
    publishDocument: async (captureDocumentId, data) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${captureDocumentId}/publish`;
      const response = await axios.post(url, data);
      return response.data;
    },

    getHistory: async (captureDocumentId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${captureDocumentId}/publish-history`;
      const response = await axios.get(url);
      return response.data;
    },
  },

  // Vendor Profile API methods
  getVendorProfiles: async () => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendor-profiles`;
    const res = await axios.get(url);
    return res.data;
  },
  getVendorProfile: async (profileId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendor-profiles/${profileId}`;
    const res = await axios.get(url);
    return res.data;
  },
  updateVendorProfile: async (profileId, data) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendor-profiles/${profileId}`;
    const res = await axios.patch(url, data);
    return res.data;
  },
  updateVendorProfileNotes: async (profileId, notes) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendor-profiles/${profileId}/notes`;
    const res = await axios.patch(url, { notes });
    return res.data;
  },
  deleteVendorProfilePattern: async (profileId, fieldKey) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendor-profiles/${profileId}/patterns/${fieldKey}`;
    const res = await axios.delete(url);
    return res.data;
  },
  bulkUpdateVendorProfiles: async (data) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendor-profiles/bulk`;
    const res = await axios.patch(url, data);
    return res.data;
  },
  resetVendorProfile: async (profileId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendor-profiles/${profileId}/reset`;
    const res = await axios.post(url);
    return res.data;
  },
  deleteVendorProfile: async (profileId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendor-profiles/${profileId}`;
    const res = await axios.delete(url);
    return res.data;
  },

  // Vendor Directory (tenant vendor records). Writes are admin-only on the
  // server and use POST, PATCH and DELETE only: the vendors router has no PUT.
  getVendors: async () => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendors`;
    const res = await axios.get(url);
    return res.data;
  },
  getVendor: async (vendorId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendors/${encodeURIComponent(vendorId)}`;
    const res = await axios.get(url);
    return res.data;
  },
  createVendor: async ({ display_name, vendor_number, printed_names }) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendors`;
    const res = await axios.post(url, { display_name, vendor_number, printed_names });
    return res.data;
  },
  updateVendor: async (vendorId, patch) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendors/${encodeURIComponent(vendorId)}`;
    const res = await axios.patch(url, patch);
    return res.data;
  },
  addVendorName: async (vendorId, printedName) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendors/${encodeURIComponent(vendorId)}/names`;
    const res = await axios.post(url, { printed_name: printedName });
    return res.data;
  },
  removeVendorName: async (vendorId, nameId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendors/${encodeURIComponent(vendorId)}/names/${encodeURIComponent(nameId)}`;
    const res = await axios.delete(url);
    return res.data;
  },
  deleteVendor: async (vendorId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendors/${encodeURIComponent(vendorId)}`;
    const res = await axios.delete(url);
    return res.data;
  },
  // Vendor import (slice 2c-2). Both POSTs take { source, file_name, rows,
  // link } and are admin-only; preview writes nothing. The import row is
  // read to follow its document re-stamp.
  previewVendorImport: async (body) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendors/import/preview`;
    const res = await axios.post(url, body);
    return res.data;
  },
  applyVendorImport: async (body) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendors/import/apply`;
    const res = await axios.post(url, body);
    return res.data;
  },
  getVendorImport: async (importId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendors/imports/${encodeURIComponent(importId)}`;
    const res = await axios.get(url);
    return res.data;
  },
  // Import recovery (slice 2d): the tenant's 20 newest import rows, and a
  // retry of an import's document update (admin only; 202 with the row).
  listVendorImports: async () => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendors/imports`;
    const res = await axios.get(url);
    return res.data;
  },
  retryVendorImportRestamp: async (importId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/vendors/imports/${encodeURIComponent(importId)}/restamp`;
    const res = await axios.post(url);
    return res.data;
  },
  // A reviewer's vendor values on one document (slice 2b-3). field is
  // display_name or vendor_number; value null clears. Both answer
  // { ok, vendor: snapshot }.
  setDocumentVendorValue: async (documentId, field, value) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${encodeURIComponent(documentId)}/vendor/${encodeURIComponent(field)}`;
    const res = await axios.put(url, { value });
    return res.data;
  },
  revertDocumentVendorValue: async (documentId, field) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${encodeURIComponent(documentId)}/vendor/${encodeURIComponent(field)}`;
    const res = await axios.delete(url);
    return res.data;
  },

  // Capture Settings API methods
  getCaptureSettings: async () => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/settings`;
    const res = await axios.get(url);
    return res.data;
  },
  updateCaptureSettings: async (data) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/settings`;
    const res = await axios.patch(url, data);
    return res.data;
  },

  // Line Items Extraction
  extractLineItems: async (documentId) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/documents/${documentId}/extract-line-items`;
    const res = await axios.post(url);
    return res.data;
  },

  // AI-assist — Template Studio advisory review (write-free) + human-gated apply.
  // Auth + tenant are auto-attached by the /api/capture request interceptor above.
  assistantReview: async ({ templateId, documentId, messages }) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/assistant/template-studio`;
    const res = await axios.post(url, { templateId, documentId, messages });
    return res.data;
  },
  assistantApply: async ({ templateId, proposal }) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/assistant/template-studio/apply`;
    const res = await axios.post(url, { templateId, proposal });
    return res.data;
  },
  // Read-only, non-persisting anchor preview: resolves the anchor on the source
  // doc and re-extracts the reproducing doc to return before/after for the field.
  assistantPreviewAnchor: async ({ templateId, documentId, field_key, anchor_text, anchor_match_mode, replace }) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/assistant/template-studio/preview-anchor`;
    const res = await axios.post(url, { templateId, documentId, field_key, anchor_text, anchor_match_mode, replace });
    return res.data;
  },

  // Per-user Index-Field display ORDER for the Review viewer. Identity is
  // server-derived; never send user_id/tenant_id. Auth + tenant are attached by
  // the /api/capture request interceptor above.
  getFieldPreferences: async ({ docType }) => {
    const qs = docType ? `?doc_type=${encodeURIComponent(docType)}` : '';
    const url = `${CAPTURE_API_BASE_URL}/api/capture/processing-monitor/field-preferences${qs}`;
    const res = await axios.get(url);
    return res.data;
  },
  saveFieldPreferences: async ({ docType, visibleKeys }) => {
    const url = `${CAPTURE_API_BASE_URL}/api/capture/processing-monitor/field-preferences`;
    const res = await axios.put(url, { doc_type: docType, visible_keys: visibleKeys });
    return res.data;
  },
  resetFieldPreferences: async ({ docType }) => {
    const qs = docType ? `?doc_type=${encodeURIComponent(docType)}` : '';
    const url = `${CAPTURE_API_BASE_URL}/api/capture/processing-monitor/field-preferences${qs}`;
    const res = await axios.delete(url);
    return res.data;
  },

  // ── Cloud ingestion channels (Document Sources settings page) ──────────
  //
  // Seven calls against capture-service's /api/capture/ingestion surface. Auth and
  // x-tenant-id are attached by the /api/capture request interceptor at the top
  // of this file; nothing here sends a tenant or a user id.
  //
  // NOTHING IS LOGGED. A channel row is close to the credential: the list
  // projection deliberately excludes the KMS blob and last_failure_code, and
  // console-dumping folder names would put the connecting admin's private
  // OneDrive structure into every browser console.
  //
  // Non-2xx REJECTS (axios default). Callers read err.response.data — the folder
  // routes in particular return two different 404s and two different 502s that
  // are only distinguishable by the body, never by the status.
  ingestion: {
    /** Provider registry + the frequency options. Member-visible. */
    listProviders: async () => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/providers`;
      const res = await axios.get(url);
      return res.data;
    },

    /**
     * The tenant's active channels. Member-visible.
     *
     * THE PATH HAS NO TRAILING SLASH AND THAT IS LOAD-BEARING. The access policy
     * carries two rules: 'ingestion:channels' on the bare prefix (any member) and
     * 'ingestion:channel-folders' on '/api/capture/ingestion/channels/' — with a
     * slash, meaning children-only, admin-only. A trailing slash here would
     * resolve this read to the admin rule and 403 every member.
     */
    listChannels: async () => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/channels`;
      const res = await axios.get(url);
      return res.data;
    },

    /**
     * What one channel has actually taken in. Member-visible.
     *
     * Returns { ok, last_item: { name, seen_at } | null, duplicates_held }.
     *
     * SEPARATE FROM listChannels on purpose. This aggregates over a ledger and
     * counts documents, which does not belong in the list's column projection —
     * and a slow ledger read must not be able to stall the list the whole page
     * needs to render. The caller fetches these per card, after the list.
     */
    channelActivity: async (channelId) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/channels/${encodeURIComponent(channelId)}/activity`;
      const res = await axios.get(url);
      return res.data;
    },

    /** Partial settings update. Admin-only server-side. Returns { ok, channel }. */
    updateChannel: async (channelId, patch) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/channels/${encodeURIComponent(channelId)}`;
      const res = await axios.patch(url, patch);
      return res.data;
    },

    /**
     * Bind a mailbox to an outlook_mailbox channel. Admin-only server-side.
     *
     * SEPARATE FROM updateChannel, because the server route is separate and for
     * a reason: it proves access with a live Graph call before writing, and it
     * stamps connected_at — the bind-time cutoff that decides which mail is old
     * enough to ignore. Routing this through the settings PATCH would skip both.
     *
     * `address` is omitted entirely for a self mailbox rather than sent as null:
     * the server clears it either way, and sending an address alongside
     * kind:'self' is a request that contradicts itself.
     */
    setMailbox: async (channelId, { kind, address = null } = {}) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/channels/${encodeURIComponent(channelId)}/mailbox`;
      const body = { mailbox_kind: kind };
      if (kind === 'shared') body.mailbox_address = address;
      const res = await axios.patch(url, body);
      return res.data;
    },

    /**
     * One page of folders. Admin-only server-side.
     *
     * `cursor` is OUR opaque token lifted from Graph's $skiptoken, never a URL —
     * the service refuses to hand out Graph URLs precisely so nothing here can be
     * talked into fetching one.
     */
    listFolders: async (channelId, { parent = null, cursor = null } = {}) => {
      const qs = new URLSearchParams();
      if (parent) qs.append('parent', parent);
      if (cursor) qs.append('cursor', cursor);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/channels/${encodeURIComponent(channelId)}/folders${suffix}`;
      const res = await axios.get(url);
      return res.data;
    },

    /**
     * One page of folders of the CONNECTED drive (the tenant's Microsoft 365
     * connection), for the OneDrive destination's folder picker. Admin-only
     * server-side. Returns { ok, drive_id, folders, next_cursor }.
     */
    listConnectionFolders: async ({ parent = null, cursor = null } = {}) => {
      const qs = new URLSearchParams();
      if (parent) qs.append('parent', parent);
      if (cursor) qs.append('cursor', cursor);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/connection/folders${suffix}`;
      const res = await axios.get(url);
      return res.data;
    },

    /**
     * Create a folder in the connected drive. Admin-only. No parentId means the
     * top level. Returns { ok, drive_id, folder }; a taken name is a 409 that
     * carries the folder that has it.
     */
    createConnectionFolder: async ({ parentId = null, name }) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/connection/folders`;
      const res = await axios.post(url, parentId ? { parent_id: parentId, name } : { name });
      return res.data;
    },

    /**
     * May this channel watch that folder? Read-only; saves nothing.
     * Returns { ok, allowed } and, when refused, { error, code }.
     */
    checkWatchFolder: async (channelId, { folderId }) => {
      const qs = new URLSearchParams({ folder_id: folderId });
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/channels/${encodeURIComponent(channelId)}/watch-folder/check?${qs.toString()}`;
      const res = await axios.get(url);
      return res.data;
    },

    /**
     * May that folder of the connected drive hold a OneDrive destination?
     * Read-only. Returns { ok, allowed } and, when refused, { error, code }.
     */
    checkConnectionFolder: async ({ driveId, folderId }) => {
      const qs = new URLSearchParams({ drive_id: driveId, folder_id: folderId });
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/connection/folder-check?${qs.toString()}`;
      const res = await axios.get(url);
      return res.data;
    },

    /**
     * Set the watch folder. REPLACES — one folder per channel.
     * Returns { ok, watch_folder_id, watch_folder_path, replaced }.
     */
    setWatchFolder: async (channelId, { folderId, folderPath }) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/channels/${encodeURIComponent(channelId)}/watch-folder`;
      const res = await axios.put(url, { folder_id: folderId, folder_path: folderPath });
      return res.data;
    },

    /**
     * The tenant's Microsoft 365 connection, or null when it has not connected.
     * Member-visible. Carries the signed-in account and the connection's status;
     * never a scope, an id of a person, or a credential.
     */
    getConnection: async () => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/connection`;
      const res = await axios.get(url);
      return res.data;
    },

    /**
     * Start the Microsoft 365 consent flow: connect when the tenant has no
     * connection, reconnect when it has one. Returns { authorize_url }.
     *
     * NO ARGUMENTS, and that is the design. There is one connection per tenant,
     * so the server decides which of the two this is from what exists. A source
     * type or a channel id used to be sent here, when each source had its own
     * consent; sending one now would suggest the caller can choose a target,
     * and it cannot.
     */
    startConnection: async () => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/oauth/start`;
      const res = await axios.post(url, {});
      return res.data;
    },

    /**
     * What disconnecting Microsoft 365 would turn off: { account, sources }.
     * Admin-only, read-only. Shown in the confirmation BEFORE anything changes.
     * 409 with code 'not_connected' when there is nothing to disconnect.
     */
    previewDisconnect: async () => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/connection/disconnect-preview`;
      const res = await axios.get(url);
      return res.data;
    },

    /**
     * Disconnect Microsoft 365. Admin-only. Turns off every source on the
     * connection and deletes the stored sign-in; nothing already captured is
     * touched. NO BODY: the connection is the workspace's, and there is no id
     * for a caller to get wrong.
     */
    disconnectConnection: async () => {
      // The BARE path, no trailing slash, like its neighbours.
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/connection`;
      const res = await axios.delete(url);
      return res.data;
    },

    /**
     * Add a OneDrive or Outlook mailbox source to the tenant's connection.
     * Admin-only. NO consent and no redirect: the response is the new channel,
     * unconfigured, ready for the mailbox step or the folder picker.
     *
     * 409 with code 'connection_required' or 'connection_reconnect_needed' when
     * the tenant has not connected, or needs to reconnect, first.
     */
    createChannel: async (channelType) => {
      // The BARE path, no trailing slash — the same care as listChannels above.
      const url = `${CAPTURE_API_BASE_URL}/api/capture/ingestion/channels`;
      const res = await axios.post(url, { channel_type: channelType });
      return res.data;
    },
  },

  feedback: {
    /**
     * The header's Feature Request button. The server adds who sent it and
     * from which tenant, and posts it to Slack and/or Teams — the webhook URLs
     * live only in capture-service's env and never reach this bundle.
     */
    submitFeatureRequest: async ({ what, why, urgency, pagePath, clientBuild }) => {
      const url = `${CAPTURE_API_BASE_URL}/api/capture/feedback/feature-request`;
      const res = await axios.post(url, { what, why, urgency, pagePath, clientBuild });
      return res.data;
    },
  },
};

// Replicate the exact working document library API pattern
export const documentAPI = {
  // Get document metadata and extracted fields (same as working library)
  getDocument: async (documentId) => {
    console.log('[Document API] Fetching document:', documentId);
    
    try {
      const response = await libraryApi.get(`/documents/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('[Document API] Request failed:', error);
      if (error.response) {
        console.error('[Document API] Response status:', error.response.status);
        console.error('[Document API] Response data:', error.response.data);
      }
      throw error;
    }
  },
};

// Field API for unified fields (same as working library)
export const fieldApi = {
  getDocumentFields: async (documentId) => {
    console.log('[Field API] Fetching unified fields:', documentId);
    
    return (await libraryApi.get(`/fields/documents/${documentId}`)).data;
  },
};

// Legacy library API for backward compatibility
export const libraryAPI = {
  // Get document metadata and extracted fields
  getDocument: async (documentId) => {
    console.log('[Library API] Fetching document:', documentId);
    
    return (await libraryApi.get(`/documents/${documentId}`)).data;
  },
  
  // Get document file for PDF viewer
  getDocumentFile: (documentId) => {
    return `${LIBRARY_API_BASE}/documents/${documentId}/file`;
  },
};

// Error handling helper
export const handleAPIError = (error, context = 'API call') => {
  console.error(`[${context}] Error:`, error);
  
  if (error.response) {
    // Server responded with error status
    const message = error.response.data?.error || error.response.data?.message || 'Server error';
    return {
      type: 'server_error',
      message,
      status: error.response.status,
      details: error.response.data,
    };
  } else if (error.request) {
    // Network error
    return {
      type: 'network_error',
      message: 'Network error - check if services are running',
      details: error.message,
    };
  } else {
    // Other error
    return {
      type: 'unknown_error',
      message: error.message || 'Unknown error occurred',
      details: error,
    };
  }
};
